/* eslint-disable @typescript-eslint/no-empty-function */
import React, { useEffect, useState } from 'react'
import { Box, Flex, Text, useDisclosure, useToast } from '@chakra-ui/react'
import { DragDropContext } from '@hello-pangea/dnd'
import { cloneDeep } from 'lodash'
import AvailableCharts from './AvailableCharts'
import { ButtonCreateNode } from './ButtonCreateNode'
import { v4 as uuid } from 'uuid'
import ConfirmAlertDialog from '../../../ui/ConfirmAlertDialog'
import { findNode, findParentNode } from '../../../../utils/CommonHelper'
import {
  IReportBlueprint,
  IReportBlueprintChart,
  IReportComponentBlueprint,
} from '../../../../services/decoders/blueprintDecoder'
import { TreeViewDroppable } from './TreeViewDroppable'
import { CreateNodeParams, TreeViewContext } from './TreeViewContext'
import { insertNodeById } from './TreeView.utils'

interface TreeViewProps {
  rootId: string
  reportBlueprint: IReportBlueprint
  availableCharts: IReportBlueprintChart[]
  onItemClick?: (data: any) => void
  onEditDrilldown?: (data: any) => void
  onDrop?: (
    data: IReportComponentBlueprint,
    target: IReportComponentBlueprint,
  ) => void
  onSetDeleteNode?: (data: IReportComponentBlueprint) => void
  onDeleteNode?: () => void
  onChangeStructure?: (value: IReportComponentBlueprint[]) => void
  readOnly?: boolean
}
const TreeView = ({
  rootId,
  reportBlueprint,
  availableCharts,
  onItemClick = () => {},
  onEditDrilldown = () => {},
  onDrop = () => {},
  onSetDeleteNode = () => {},
  onDeleteNode = () => {},
  readOnly = false,
  onChangeStructure = (value: IReportComponentBlueprint[]) => {},
}: TreeViewProps) => {
  const toast = useToast({
    position: 'bottom-right',
    variant: 'left-accent',
  })
  const [selectedNodeId, setSelectedNodeId] = useState<string>('')
  const [deleteItem, setDeleteItem] =
    useState<IReportComponentBlueprint | null>(null)
  const [lastAddedNodeId, setLastAddedNodeId] = useState<string | undefined>(
    undefined,
  )

  const isDragging = React.useRef(false)
  const [rootSections, setRootSections] = useState<IReportComponentBlueprint[]>(
    [],
  )
  useEffect(() => {
    if (reportBlueprint.children) {
      setRootSections(cloneDeep(reportBlueprint.children))
    }
  }, [reportBlueprint.children])

  const [currentType, setCurrentType] = useState('MAIN')

  const {
    onOpen: onOpenNodePopover,
    onClose: onCloseNodePopover,
    isOpen: showNodePopover,
  } = useDisclosure()

  const handleItemClick = ({ id, type }) => {
    if (readOnly) {
      return
    }
    setCurrentType(type)

    if (onItemClick) {
      onItemClick({ id, type })
      setSelectedNodeId(id)
    }
  }

  const handleDragStart = () => {
    isDragging.current = true
  }

  const handleBeforeCapture = () => {
    if (isDragging.current) {
      toast({
        title: 'You are dragging an item',
        description: 'Please drop it before start dragging another item',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      })
      return null
    }
  }

  const handleDeleteItem = data => {
    setDeleteItem(data)
    onSetDeleteNode(data)
  }

  const handleEditDrilldown = data => {
    onEditDrilldown(data)
  }

  const handleDeleteConfirm = () => {
    if (!deleteItem) {
      toast({
        title: 'Error',
        description: 'Delete Item invalid',
        status: 'error',
        duration: 5000,
      })
      return
    }

    if (deleteItem.type === 'section') {
      /* Root level section */
      const nodeIndex = rootSections.findIndex(
        (child: IReportComponentBlueprint) => child.id === deleteItem.id,
      )
      rootSections.splice(nodeIndex, 1)
      const newStructure = cloneDeep(rootSections)
      setRootSections(newStructure)
      setDeleteItem(null)
      onChangeStructure(newStructure)
      onDeleteNode()
      toast({
        title: 'Deleted',
        description: `Root section ${deleteItem.name} deleted`,
        status: 'success',
        duration: 5000,
      })
      return
    }

    let parentNode
    let nodeIndex
    if (deleteItem.parentId === deleteItem.blueprintId) {
      // Feels like the deleteItem.type === 'section' part covers this already, but oh well
      /* Root level section */
      nodeIndex = rootSections.findIndex(
        (child: IReportComponentBlueprint) => child.id === deleteItem.id,
      )
      if (nodeIndex !== -1)
        rootSections.splice(nodeIndex, 1)
    }
    else {
      if (deleteItem.parentId === null || typeof (deleteItem.parentId) == 'undefined') {
        parentNode = findParentNode(deleteItem.id, rootSections)
        if (parentNode) {
          deleteItem.parentId = parentNode.id
        }
      }

      if (deleteItem.parentId) {
        if (!parentNode)
          parentNode = findNode(deleteItem.parentId, rootSections)
        if (!parentNode) {
          toast({
            title: 'Error',
            description: 'Parent node not found',
            status: 'error',
            duration: 5000,
          })
          return
        }

        nodeIndex = parentNode.children?.findIndex(
          child => child.id === deleteItem.id,
        )

        if (parentNode && nodeIndex !== -1) {
          parentNode.children.splice(nodeIndex, 1)
        }
      }
    }
    const newStructure = cloneDeep(rootSections)
    setRootSections(newStructure)
    setDeleteItem(null)
    onChangeStructure(newStructure)
    onDeleteNode()
    toast({
      title: 'Deleted',
      description: `Item ${deleteItem.type}: ${deleteItem.name} deleted`,
      status: 'success',
      duration: 5000,
    })
  }

  const handleDragEnd = result => {
    isDragging.current = false
    if (!result.destination) {
      toast({
        title: 'Info',
        description:
          'Click in the item to select it, then drag it to reorder or add',
        status: 'info',
        duration: 5000,
      })
      return
    }

    const droppableId = result.destination?.droppableId
    if (
      (result.source.droppableId.includes(droppableId) &&
        result.source.index === result.destination.index) ||
      !droppableId
    ) {
      toast({
        title: 'Error',
        description: 'Destination is the same as source',
        status: 'error',
        duration: 5000,
      })
      return
    }
    /**
     * Reorder root sections
     */
    if (result.type === 'MAIN') {
      const rootReordered = reorder(
        rootSections,
        result.source.index,
        result.destination.index,
      )

      setRootSections(rootReordered)
      onChangeStructure(rootReordered)
      toast({
        title: 'Success',
        description: `Item ${rootSections[result.source.index].name} reordered`,
        status: 'success',
        duration: 5000,
      })
      return
    }

    /**
     * Items dragged inside the Structure droppable area
     */
    if (
      (['subsection', 'chart', 'drilldown'].includes(result.type) ||
        result.type?.startsWith('drilldown')) &&
      result.source.droppableId === 'available'
    ) {
      const parentNode = findNode(result.destination.droppableId, rootSections)
      if (!parentNode) {
        toast({
          title: 'Error',
          description: 'Parent target node not found',
          status: 'error',
          duration: 5000,
        })
        return
      }

      /**
       * Will clone an available item to be added to the structure
       * this item is a ReportChartStructure having columns and rows
       */
      const cloneItem = cloneDeep(
        availableCharts.find(item => item.id === result.draggableId),
      )
      if (!cloneItem) {
        toast({
          title: 'Error',
          description: 'Could not find item to copy into structure',
          status: 'error',
          duration: 5000,
        })
        return
      }

      /**
       * All items dropped needs a unique id to be identified
       * because they are a copy of the original item
       */
      const copyItem: IReportComponentBlueprint = {
        id: uuid(),
        blueprintId: parentNode.blueprintId,
        parentId: parentNode.id,
        name: cloneItem.name,
        componentType: cloneItem.type.localeCompare('chart') === 0 ? 2 : cloneItem.componentType,
        order: 99,
        children: [] as IReportComponentBlueprint[],
        filters: [] as string[],
        drillDownInfo: cloneItem.drillDownInfo,
        type: cloneItem.type,
        selected: false,
        permitAdd: true,
        objectAdd: {
          title: 'New',
          type: cloneItem.type,
          droppable: true,
        },
        colour: '',
      }

      if (!parentNode.children) {
        parentNode.children = []
      }
      /**
       * Fix the type of the item if the parentNode is a chart or a drilldown
       */

      if (
        ['chart', 'drilldown'].includes(parentNode.type) ||
        parentNode.type?.startsWith('drilldown')
      ) {
        const id = `drilldown-${uuid()}`
        copyItem.type = id
        copyItem.id = id
        copyItem.deletable = true
        copyItem.parentId = parentNode.id
        copyItem.componentType = 2

        /**
         * If the parent is a chart or drilldown, the item will be added as a drilldown AND as a child (to be displayed)
         */
        parentNode.drillDownInfo = {
          type: 0,
          linkages: {
            '0': {
              chartName: copyItem.name,
              additionalFilters: [],
            },
          },
        }

        /**
         * TODO: The backend should return this information separated
         */
        copyItem.columns = cloneItem.columns
        copyItem.rows = cloneItem.rows

        setLastAddedNodeId(copyItem.id)
        onOpenNodePopover()
      }

      parentNode.children.splice(result.destination.index, 0, copyItem)
      const newStructure = cloneDeep(rootSections)
      setRootSections(newStructure)
      onChangeStructure(newStructure)
      onDrop(copyItem, parentNode)
      toast({
        title: 'Success',
        description: `Item ${cloneItem.name} added`,
        status: 'success',
        duration: 5000,
      })
      return
    }

    const nested = findNode(result.source.droppableId, rootSections)
    if (!nested) {
      //eslint-disable-next-line

      toast({
        title: 'Error',
        description: 'Charts can only be added to subsections',
        status: 'error',
        duration: 5000,
      })
      return
    }

    nested.children = reorder(
      nested.children,
      result.source.index,
      result.destination.index,
    )

    const newStructure = cloneDeep(rootSections)
    setRootSections(newStructure)
    onChangeStructure(newStructure)
    toast({
      title: 'Success',
      description: `Item ${nested.name} reordered`,
      status: 'success',
      duration: 5000,
    })
  }

  const handleCreateNode = ({
    nodeTitle,
    parentId,
    type,
  }: CreateNodeParams) => {
    const newNode: IReportComponentBlueprint = {
      id: uuid(),
      blueprintId: rootId,
      parentId: parentId || rootId,
      name: nodeTitle,
      componentType: ['section', 'subsection'].includes(type) ? 1 : 2,
      order: 99,
      type,
      children: [],
      filters: [],
      drillDownInfo: {
        linkages: {},
        type: 0,
      },
      selected: false,
      permitAdd: true,
      objectAdd: {
        title: 'New',
        type: type,
        droppable: true,
      },
      colour: '',
    }

    const blueprint = cloneDeep(reportBlueprint)
    insertNodeById(blueprint, parentId, newNode)
    onChangeStructure(blueprint.children)
    setRootSections(blueprint.children)
    toast({
      title: 'Success',
      description: `Item "${nodeTitle}" created.`,
      status: 'success',
      duration: 5000,
    })
  }

  if (rootSections?.length === 0) <Box>Loading...</Box>

  return (
    <TreeViewContext.Provider
      value={{
        onItemClick: handleItemClick,
        onCreateNode: handleCreateNode,
        onDelete: handleDeleteItem,
        onEditDrilldown: handleEditDrilldown,
        readOnly,
        currentType,
        selectedNodeId,
        onDeleteNode,
        lastAddedNodeId,
        showNodePopover,
        onOpenNodePopover,
        onCloseNodePopover,
      }}>
      <DragDropContext
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onBeforeCapture={handleBeforeCapture}>
        <Flex>
          <Box flex={1}>
            <Text
              textStyle="h3"
              mb="12px">
              Current Structure:
            </Text>
            <Box
              p="12px 0"
              border="1px solid"
              borderColor="brand.gray"
              maxH="500px"
              minH="400px"
              overflowY="auto">
              <TreeViewDroppable
                data={reportBlueprint.children}
                parentId={rootId}
                type="MAIN"
                droppableId="main"
              />
              <Box ml="60px">
                <ButtonCreateNode
                  onSubmit={handleCreateNode}
                  target={rootSections}
                  parentId={rootId}
                  type="section"
                  label="Add Section"
                />
              </Box>
            </Box>
          </Box>
          {!readOnly && (
            <Box
              flex={1}
              p="0 32px 32px">
              <AvailableCharts chartList={availableCharts} />
            </Box>
          )}
        </Flex>
      </DragDropContext>

      <ConfirmAlertDialog
        isOpen={!!deleteItem}
        onDismiss={() => setDeleteItem(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete item"
        description={`Deleting the item ${deleteItem?.name}`}
      />
    </TreeViewContext.Provider>
  )
}

const reorder = (structure, startIndex, endIndex) => {
  const result = cloneDeep(structure)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)

  return result
}

export default TreeView
